// ==========================================================================
// lib/reconcile.ts — the Xero-style reconcile flow (FF-039, 9 Oct 2026)
//   Every unreconciled bank line gets a suggestion, in this order:
//     1. Transfer: the opposite amount in another of our accounts within 5 days
//     2. Rule: "always code descriptions containing X as Y" (coding_rules)
//     3. Same as last time: how this merchant was coded before
//   OK applies it. Receipt matching slots in here with FF-035.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { assertCompleteRows } from "./aggregateState";
import { useHouseholdId } from "./household";
import { merchantKey, splitDescription, type Txn } from "./transactions";
import { contactFor, type Contact } from "./contacts";
import { receiptFor, type Receipt } from "./receipts";
import type { LedgerLine } from "./rental";
import type { ExpectedSale } from "./vehicles";

export type Rule = { id: string; match_text: string; account_id: string | null; category_id: string; tracker_id: string | null; asset_id: string | null; contact_id: string | null };
export type Summary = {
  account_id: string; to_reconcile: number; txn_count: number; first_date: string | null; last_date: string | null;
  statement_date: string | null; statement_balance_cents: number | null; statement_source: "csv" | "manual" | null; app_sum_to_statement_cents: number;
};
export type Coding = { category_id: string; tracker_id: string | null; asset_id: string | null; contact_id: string | null };
export type Suggestion =
  | { kind: "transfer"; other: Txn }
  | { kind: "rule"; rule: Rule; coding: Coding }
  | { kind: "contact"; contact: Contact; coding: Coding }
  | { kind: "receipt"; receipt: Receipt; coding: Coding }
  | { kind: "payout"; payout: LedgerLine; coding: Coding }
  | { kind: "sale"; sale: ExpectedSale; coding: Coding }
  | { kind: "history"; coding: Coding };

function useInvalidate() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { for (const k of ["txns", "split_totals", "unreconciled", "rec_summary", "rules", "audit", "contacts", "contact_totals", "receipts", "rental", "sales", "vehicles"]) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

/** Every line not yet coded, paired or excluded, oldest first (paged past the 1,000-row API limit). */
export function useUnreconciled() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["unreconciled", hid], enabled: !!hid,
    queryFn: async () => {
      const out: Txn[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from("transactions")
          .select("id,account_id,posted_on,amount_cents,description,balance_cents,status,transfer_pair_id,note,transaction_splits(id,amount_cents,category_id,tracker_id,asset_id,note)")
          .eq("household_id", hid).is("deleted_at", null).eq("status", "new").is("transfer_pair_id", null)
          .order("posted_on", { ascending: true }).order("created_at", { ascending: true }).range(from, from + 999);
        if (error) throw error;
        out.push(...(data as unknown as Txn[]).map((t) => ({ ...t, amount_cents: Number(t.amount_cents), balance_cents: t.balance_cents === null ? null : Number(t.balance_cents) })));
        if ((data ?? []).length < 1000) break;
      }
      return out;
    },
  });
}

export function useRecSummary() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rec_summary", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error, count } = await supabase.rpc("reconcile_summary", { p_household: hid }, { count: "exact" });
      if (error) throw error;
      assertCompleteRows({ data, count }, "Balances");
      return (data as Summary[]).map((s) => ({
        ...s, to_reconcile: Number(s.to_reconcile), txn_count: Number(s.txn_count), app_sum_to_statement_cents: Number(s.app_sum_to_statement_cents),
        statement_balance_cents: s.statement_balance_cents === null ? null : Number(s.statement_balance_cents),
      }));
    },
  });
}

export function useRules() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rules", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("coding_rules").select("id,match_text,account_id,category_id,tracker_id,asset_id,contact_id").eq("household_id", hid).is("archived_at", null).order("match_text");
      if (error) throw error;
      return data as Rule[];
    },
  });
}

/** Default rule text for a line: the merchant part, lower case ("woolworths kedron"). */
export const ruleTextFor = (desc: string) => splitDescription(desc).merchant.toLowerCase().replace(/\s+/g, " ").trim().slice(0, 60);

export function suggest(t: Txn, all: Txn[], rules: Rule[], history: Map<string, Coding[]> | undefined, contacts?: Contact[], receipts?: Receipt[],
  rental?: { payouts: LedgerLine[]; code945: string | undefined }, sales?: { expected: ExpectedSale[]; code940: string | undefined }): Suggestion | null {
  const base = suggestCode(t, all, rules, history, contacts);
  if (base?.kind === "transfer") return base;
  // Money in from selling an asset (032): same amount, the account it was meant to land in, within 3 weeks.
  const sale = saleFor(t, sales?.expected);
  if (sale && sales?.code940) return { kind: "sale", sale, coding: { category_id: sales.code940, tracker_id: null, asset_id: sale.id, contact_id: base ? base.coding.contact_id : contactFor(t.description, contacts)?.id ?? null } };
  // An agent's "Payment to Owner" from an approved rental statement (FF-036 part 2):
  // code it 945 (a money movement) so the rent is counted once, from the ledger.
  const pay = payoutFor(t, rental?.payouts);
  if (pay && rental?.code945) return { kind: "payout", payout: pay, coding: { category_id: rental.code945, tracker_id: null, asset_id: pay.asset_id, contact_id: base ? base.coding.contact_id : contactFor(t.description, contacts)?.id ?? null } };
  // A filled-in receipt for the same amount wins: it's the best evidence there is.
  const rec = receiptFor(t, receipts);
  if (rec) {
    const fallback = base && base.kind !== "receipt" ? base.coding : null;
    const category_id = rec.category_id ?? fallback?.category_id ?? "";
    if (category_id) return { kind: "receipt", receipt: rec, coding: {
      category_id, tracker_id: rec.tracker_id ?? (rec.category_id ? null : fallback?.tracker_id ?? null),
      asset_id: rec.asset_id ?? null, contact_id: rec.contact_id ?? fallback?.contact_id ?? contactFor(t.description, contacts)?.id ?? null,
    } };
  }
  return base;
}

/** A sale waiting for its money: the full price, into the account named on the sale, within 21 days. */
export function saleFor(t: Txn, expected: ExpectedSale[] | undefined): ExpectedSale | undefined {
  if (t.amount_cents <= 0 || !expected?.length) return undefined;
  const d = new Date(t.posted_on).getTime();
  const near = (x: ExpectedSale) => Math.abs(new Date(x.proceeds_expected_on ?? x.disposed_on).getTime() - d);
  return expected.filter((x) => x.proceeds_account_id === t.account_id && x.sold_price_cents === t.amount_cents && near(x) <= 21 * 86400000).sort((a, b) => near(a) - near(b))[0];
}

/** An unlinked ledger payout for the same amount within 7 days of this deposit. */
export function payoutFor(t: Txn, payouts: LedgerLine[] | undefined): LedgerLine | undefined {
  if (t.amount_cents <= 0 || !payouts?.length) return undefined;
  const d = new Date(t.posted_on).getTime();
  return payouts.filter((p) => !p.transaction_id && -p.amount_cents === t.amount_cents && Math.abs(new Date(p.entry_date).getTime() - d) <= 7 * 86400000)
    .sort((a, b) => Math.abs(new Date(a.entry_date).getTime() - d) - Math.abs(new Date(b.entry_date).getTime() - d))[0];
}

function suggestCode(t: Txn, all: Txn[], rules: Rule[], history: Map<string, Coding[]> | undefined, contacts?: Contact[]): Suggestion | null {
  const d = new Date(t.posted_on).getTime();
  const others = all.filter((o) => o.id !== t.id && o.account_id !== t.account_id && o.amount_cents === -t.amount_cents && Math.abs(new Date(o.posted_on).getTime() - d) <= 5 * 86400000);
  if (others.length >= 1) {
    others.sort((a, b) => Math.abs(new Date(a.posted_on).getTime() - d) - Math.abs(new Date(b.posted_on).getTime() - d));
    return { kind: "transfer", other: others[0] };
  }
  const desc = t.description.toLowerCase();
  const rule = rules.filter((r) => (!r.account_id || r.account_id === t.account_id) && desc.includes(r.match_text)).sort((a, b) => b.match_text.length - a.match_text.length)[0];
  const who = contactFor(t.description, contacts);
  if (rule) return { kind: "rule", rule, coding: { category_id: rule.category_id, tracker_id: rule.tracker_id, asset_id: rule.asset_id, contact_id: rule.contact_id ?? who?.id ?? null } };
  if (who?.default_category_id) return { kind: "contact", contact: who, coding: { category_id: who.default_category_id, tracker_id: who.default_tracker_id, asset_id: who.default_asset_id ?? null, contact_id: who.id } };
  const h = history?.get(merchantKey(t.description))?.[0];
  if (h) return { kind: "history", coding: { ...h, contact_id: h.contact_id ?? who?.id ?? null } };
  return null;
}

export type Action =
  | { t: Txn; kind: "code"; coding: Coding; saveRule?: string; learn?: { contact: Contact; text: string }; receiptId?: string; ledgerId?: string; saleAssetId?: string }
  | { t: Txn; kind: "transfer"; other: Txn }
  | { t: Txn; kind: "exclude" };

/** Apply one or many reconcile actions, then refresh once. */
export function useReconcile() {
  const hid = useHouseholdId(); const inv = useInvalidate();
  return useMutation({
    mutationFn: async (actions: Action[]) => {
      const paired = new Set<string>();
      for (const a of actions) {
        if (a.kind === "code") {
          const { error } = await supabase.rpc("code_transaction", {
            p_transaction_id: a.t.id, p_note: null, p_contact_id: a.coding.contact_id,
            p_splits: [{ amount_cents: a.t.amount_cents, category_id: a.coding.category_id, tracker_id: a.coding.tracker_id ?? "", asset_id: a.coding.asset_id ?? "", note: "" }],
          });
          if (error) throw error;
          if (a.saveRule && a.saveRule.trim().length >= 3) {
            const text = a.saveRule.trim().toLowerCase();
            const { data: ex } = await supabase.from("coding_rules").select("id").eq("household_id", hid).eq("match_text", text).is("archived_at", null).maybeSingle();
            const row = { category_id: a.coding.category_id, tracker_id: a.coding.tracker_id, asset_id: a.coding.asset_id, contact_id: a.coding.contact_id };
            const { error: re } = ex
              ? await supabase.from("coding_rules").update(row).eq("id", (ex as { id: string }).id)
              : await supabase.from("coding_rules").insert({ ...row, household_id: hid, match_text: text });
            if (re) throw re;
          }
          if (a.ledgerId) {
            const { error: le } = await supabase.from("property_ledger").update({ transaction_id: a.t.id }).eq("id", a.ledgerId).is("transaction_id", null);
            if (le) throw le;
          }
          if (a.saleAssetId) {
            const { error: se } = await supabase.from("assets").update({ proceeds_transaction_id: a.t.id }).eq("id", a.saleAssetId).is("proceeds_transaction_id", null);
            if (se) throw se;
          }
          if (a.receiptId) {
            const { error: le } = await supabase.from("receipts").update({ transaction_id: a.t.id }).eq("id", a.receiptId);
            if (le) throw le;
          }
          // Teach the contact: this description (so the next one is recognised),
          // and this code as its usual code if it hasn't got one ("Woolworths = Food").
          if (a.learn) {
            const c = a.learn.contact; const patch: { match_texts?: string[]; default_category_id?: string; default_tracker_id?: string | null } = {};
            const d = a.t.description.toLowerCase();
            if (a.learn.text.length >= 3 && !c.match_texts.some((m) => d.includes(m))) patch.match_texts = [...c.match_texts, a.learn.text];
            // Banks and family members carry many kinds of lines (fees AND interest,
            // groceries AND transfers), so they never learn a usual code (audit 9 Oct).
            if (!c.default_category_id && !["bank", "family"].includes(c.kind)) { patch.default_category_id = a.coding.category_id; patch.default_tracker_id = a.coding.tracker_id; }
            if (Object.keys(patch).length) {
              const { error: ce } = await supabase.from("contacts").update(patch).eq("id", c.id);
              if (ce) throw ce;
              Object.assign(c, patch);
            }
          }
        } else if (a.kind === "transfer") {
          if (paired.has(a.t.id) || paired.has(a.other.id)) continue;   // the other side was in the same batch
          const { error: e1 } = await supabase.from("transactions").update({ transfer_pair_id: a.other.id }).eq("id", a.t.id); if (e1) throw e1;
          const { error: e2 } = await supabase.from("transactions").update({ transfer_pair_id: a.t.id }).eq("id", a.other.id); if (e2) throw e2;
          paired.add(a.t.id); paired.add(a.other.id);
        } else {
          const { error } = await supabase.from("transactions").update({ status: "excluded" }).eq("id", a.t.id); if (error) throw error;
        }
      }
    },
    onSuccess: inv, onError: inv,
  });
}

export function useArchiveRule() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("coding_rules").update({ archived_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useSaveStatement() {
  const hid = useHouseholdId(); const inv = useInvalidate();
  return useMutation({
    mutationFn: async (a: { accountId: string; date: string; cents: number; note?: string }) => {
      const { error } = await supabase.from("account_statements").upsert(
        { household_id: hid, account_id: a.accountId, statement_date: a.date, closing_balance_cents: a.cents, source: "manual", note: a.note ?? null },
        { onConflict: "account_id,statement_date" });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** Set the account's opening balance so the app agrees with this statement (first time only). */
export function useOpeningFromStatement() {
  const qc = useQueryClient(); const hid = useHouseholdId(); const inv = useInvalidate();
  return useMutation({
    mutationFn: async (a: { accountId: string; openingCents: number; openingDate: string }) => {
      const { error } = await supabase.from("financial_accounts").update({ opening_balance_cents: a.openingCents, opening_date: a.openingDate }).eq("id", a.accountId);
      if (error) throw error;
    },
    onSuccess: () => { inv(); void qc.invalidateQueries({ queryKey: ["accounts", hid] }); },
  });
}

// ---------------------------------------------------------------------------
// History: what's been reconciled in an account, and undo (9 Oct 2026)
// James: "Should be able to see the accounts reconciliation history, for
// auditing and tracking".
// ---------------------------------------------------------------------------
export type DoneLine = Txn & { posted_label?: string };

/** Reconciled lines for one account (coded, transfer or excluded), newest first. */
export function useReconciled(accountId: string, limit: number) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["txns", hid, "reconciled", accountId, limit], enabled: !!hid && !!accountId,
    queryFn: async () => {
      const { data, error, count } = await supabase.from("transactions")
        .select("id,account_id,posted_on,amount_cents,description,balance_cents,status,transfer_pair_id,note,contact_id,card_id,transaction_splits(id,amount_cents,category_id,tracker_id,asset_id,note)", { count: "exact" })
        .eq("household_id", hid).eq("account_id", accountId).is("deleted_at", null)
        .or("status.neq.new,transfer_pair_id.not.is.null")
        .order("posted_on", { ascending: false }).order("created_at", { ascending: false }).range(0, limit - 1);
      if (error) throw error;
      const rows = (data as unknown as Txn[]).map((t) => ({ ...t, amount_cents: Number(t.amount_cents), balance_cents: t.balance_cents === null ? null : Number(t.balance_cents), transaction_splits: t.transaction_splits.map((s) => ({ ...s, amount_cents: Number(s.amount_cents) })) }));
      return { rows, total: count ?? rows.length };
    },
  });
}

/** The other side of transfer pairs, for showing "to / from Offset". */
export function usePairAccounts(ids: string[]) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["txns", hid, "pairs", ids.join(",")], enabled: !!hid && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from("transactions").select("id,account_id").in("id", ids);
      if (error) throw error;
      return new Map((data as { id: string; account_id: string }[]).map((r) => [r.id, r.account_id]));
    },
  });
}

export function useStatements(accountId: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["rec_summary", hid, "statements", accountId], enabled: !!hid && !!accountId,
    queryFn: async () => {
      const { data, error } = await supabase.from("account_statements").select("id,statement_date,closing_balance_cents,source,created_at").eq("account_id", accountId).order("statement_date", { ascending: false });
      if (error) throw error;
      return (data as { id: string; statement_date: string; closing_balance_cents: number; source: string; created_at: string }[]).map((s) => ({ ...s, closing_balance_cents: Number(s.closing_balance_cents) }));
    },
  });
}

/** Put a line back on the to-reconcile list: clears its coding, transfer pair, exclusion and receipt link. */
export function useUnreconcile() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (t: Txn) => {
      if (t.transfer_pair_id) {
        const { error: e1 } = await supabase.from("transactions").update({ transfer_pair_id: null }).eq("id", t.id); if (e1) throw e1;
        const { error: e2 } = await supabase.from("transactions").update({ transfer_pair_id: null }).eq("id", t.transfer_pair_id); if (e2) throw e2;
      }
      const { error } = await supabase.rpc("code_transaction", { p_transaction_id: t.id, p_splits: [], p_note: null, p_contact_id: null });
      if (error) throw error;
      await supabase.from("receipts").update({ transaction_id: null }).eq("transaction_id", t.id);
      await supabase.from("property_ledger").update({ transaction_id: null }).eq("transaction_id", t.id);
    },
    onSuccess: inv,
  });
}
